import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * WHAT'S NEW WHISPER — calm, dismissible.
 *
 * Hard rules:
 *  - No engagement loop. No counters, no "tap to learn more".
 *  - Three lines, plain text. No animation beyond a quiet fade.
 *  - Auto-disappears 7 days after first view, or on dismiss.
 *  - Bump VERSION to surface a new whisper. Old key stays buried.
 *  - Authed users only. Guests see nothing.
 */

const VERSION = 'v1';
const FIRST_SEEN_KEY = `lk_whats_new_${VERSION}_first_seen_at`;
const DISMISSED_KEY = `lk_whats_new_${VERSION}_dismissed`;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const ITEMS = [
  { text: 'Flyers — share an activity as a PDF or image.' },
  { text: "Running late? Send a quiet signal without a message." },
  { text: 'People — your contacts and circles now live in Backpack.' },
];

export default function WhatsNewWhisper({ user }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem(DISMISSED_KEY) === '1') return;

      let firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
      if (!firstSeen) {
        firstSeen = String(Date.now());
        localStorage.setItem(FIRST_SEEN_KEY, firstSeen);
      }
      const age = Date.now() - Number(firstSeen);
      if (age > SEVEN_DAYS_MS) return;

      setVisible(true);
    } catch {
      // localStorage unavailable — fail silently, no whisper shown
    }
  }, [user]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch (e) { void e; }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="mx-4 mt-5 rounded-xl px-4 py-3"
      style={{
        backgroundColor: 'var(--fy-surface-card)',
        border: '1px solid var(--fy-border-subtle)',
      }}
      data-testid="whats-new-whisper"
    >
      <div className="flex items-start justify-between gap-3">
        <p
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: 'var(--fy-text-disabled)' }}
        >
          What's new
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss what's new"
          className="-mt-0.5 -mr-1 p-1 rounded-md hover:opacity-70 transition-opacity"
          data-testid="whats-new-dismiss-btn"
        >
          <X className="w-3.5 h-3.5" style={{ color: 'var(--fy-text-disabled)' }} />
        </button>
      </div>
      <ul className="mt-2 space-y-1.5">
        {ITEMS.map((item, i) => (
          <li
            key={i}
            className="text-sm flex gap-2 items-start"
            style={{ color: 'var(--fy-text-secondary)' }}
            data-testid={`whats-new-item-${i}`}
          >
            <span
              aria-hidden="true"
              className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
              style={{ backgroundColor: 'var(--lk-sage)', opacity: 0.6 }}
            />
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
